//@ts-expect-error side effect import of css
import '@repo/ui/fonts/gotham';

import { ReactNode } from 'react';

import {
	QueryClient,
	QueryClientProvider,
	useQuery,
} from '@tanstack/react-query';
import {
	createRouter,
	ErrorComponent,
	RouterProvider,
} from '@tanstack/react-router';

import { CssBaseline, LinearProgress, ThemeProvider } from '@mui/material';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { ConfirmProvider } from 'material-ui-confirm';

import { OidcUserStatus } from '@axa-fr/react-oidc';

import { useUser, useUserLoadingState } from '@repo/auth';
import { AuthProvider } from '@repo/auth/AuthProvider';
import { UserProvider } from '@repo/auth/UserProvider';
import { FullPageErrorBoundary } from '@repo/ui/FullPageErrorBoundary';
// Import theme module fully to ensure MUI module augmentations are loaded
import { theme } from '@repo/ui/theme';

import { routeTree } from '~/routeTree.gen.ts';
import { config } from './auth/oidc.config';

import type {} from '@repo/features/types';

import { Locale } from 'date-fns/locale';

import { FullPageMessage } from '@repo/ui/FullPageMessage';

window.addEventListener('vite:preloadError', () => {
	window.location.reload();
});

const queryClient = new QueryClient({
	defaultOptions: {
		queries: {
			retry: false,
		},
	},
});

const setTitle = (section: string, page?: string) => ({
	meta: [
		{
			title: `MDDi - ${section}${page ? ' // ' + page : ''}`,
		},
	],
});

const router = createRouter({
	routeTree,
	context: {
		queryClient,
		user: null,
		setTitle,
	},
	defaultErrorComponent: ({ error }) => <ErrorComponent error={error} />,
	defaultPreload: 'intent',
	defaultPreloadStaleTime: 0,
	scrollRestoration: true,
	scrollToTopSelectors: ['#scrollContainer'],
	defaultHashScrollIntoView: { behavior: 'smooth' },
});

declare module '@tanstack/react-router' {
	interface Register {
		router: typeof router;
	}
}

const WithAuthApp = () => {
	const oidcUserLoadingState = useUserLoadingState();

	const user = useUser();

	switch (oidcUserLoadingState) {
		case OidcUserStatus.Loading:
			return <LinearProgress />;
		case OidcUserStatus.Unauthenticated:
			return <LinearProgress />;

		default:
			return <RouterProvider router={router} context={{ user }} />;
	}
};

const getLocale = async (): Promise<Locale> => {
	const locale = navigator.language.split('-', 2);

	const localeImported = await (
		import(`./locales/${locale.join('')}.ts`) as Promise<{
			default: Locale;
		}>
	).catch(() => null);
	if (!localeImported) {
		return (await import(`./locales/enUS.ts`)).default;
	}
	return localeImported.default;
};

const AppLocale = ({ children }: { children: ReactNode }) => {
	const { data: locale } = useQuery({
		queryKey: ['locale'],
		queryFn: getLocale,
	});

	return (
		<LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={locale}>
			{children}
		</LocalizationProvider>
	);
};

const App = () => {
	return (
		<ThemeProvider theme={theme}>
			<CssBaseline />
			<FullPageErrorBoundary>
				<AuthProvider config={config}>
					<QueryClientProvider client={queryClient}>
						<UserProvider
							logoutMessage={<FullPageMessage message="Logging out..." />}
						>
							<AppLocale>
								<ConfirmProvider
									defaultOptions={{
										confirmationButtonProps: {
											color: 'button',
										},
										cancellationButtonProps: {
											color: 'button',
											variant: 'outlined',
										},
									}}
								>
									<WithAuthApp />
								</ConfirmProvider>
							</AppLocale>
						</UserProvider>
					</QueryClientProvider>
				</AuthProvider>
			</FullPageErrorBoundary>
		</ThemeProvider>
	);
};

export default App;
